'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { signInWithPin } from '@/lib/portalAuth';

type PinRole = 'reseller' | 'manager';

export default function PortalLoginClient() {
  const router = useRouter();

  // PIN login state
  const [pinRole, setPinRole] = useState<PinRole>('reseller');
  const [loginName, setLoginName] = useState('');
  const [pin, setPin] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handlePinSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pin.length < 4) { setError('Pincode moet minimaal 4 cijfers zijn.'); return; }
    setLoading(true);
    setError('');
    try {
      const session = await signInWithPin(loginName, pin, pinRole);
      if (session.type === 'reseller') {
        router.push('/portal/reseller');
      } else {
        router.push('/portal/restaurant-owner');
      }
    } catch (err: any) {
      setError(err.message || 'Inloggen mislukt.');
    } finally {
      setLoading(false);
    }
  };

  const handlePinKey = (digit: string) => {
    if (pin.length < 6) setPin((p) => p + digit);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0F] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background glows */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[-15%] left-[25%] w-[700px] h-[700px] rounded-full bg-violet-700/8 blur-[140px]" />
        <div className="absolute bottom-[-10%] right-[15%] w-[500px] h-[500px] rounded-full bg-indigo-600/6 blur-[120px]" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-700 flex items-center justify-center shadow-xl shadow-violet-600/25">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
            </div>
            <div className="text-left">
              <p className="text-white font-bold text-xl leading-tight">HorecaPOS</p>
              <p className="text-white/40 text-xs font-medium">Partner Portaal</p>
            </div>
          </div>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur-sm">
          <form onSubmit={handlePinSubmit} className="space-y-5">
            {/* Role selector */}
            <div>
              <label className="block text-sm font-500 text-white/70 mb-2">Inloggen als</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setPinRole('reseller')}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-600 border transition-all ${pinRole === 'reseller' ? 'bg-violet-600/20 border-violet-500/50 text-violet-300' : 'bg-white/3 border-white/8 text-white/40 hover:text-white/70'}`}
                >
                  Reseller
                </button>
                <button
                  type="button"
                  onClick={() => setPinRole('manager')}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-600 border transition-all ${pinRole === 'manager' ? 'bg-violet-600/20 border-violet-500/50 text-violet-300' : 'bg-white/3 border-white/8 text-white/40 hover:text-white/70'}`}
                >
                  Manager
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-500 text-white/70 mb-2">Naam</label>
              <input
                type="text"
                value={loginName}
                onChange={(e) => setLoginName(e.target.value)}
                placeholder={pinRole === 'reseller' ? 'Jouw reseller naam' : 'Jouw manager naam'}
                required
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/25 text-sm focus:outline-none focus:border-violet-500/60 transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-500 text-white/70 mb-2">Pincode</label>
              {/* PIN display */}
              <div className="flex gap-2 justify-center mb-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className={`w-10 h-10 rounded-xl border flex items-center justify-center text-lg font-bold transition-all ${
                      i < pin.length
                        ? 'bg-violet-600/20 border-violet-500/60 text-violet-300'
                        : 'bg-white/3 border-white/10 text-white/20'
                    }`}
                  >
                    {i < pin.length ? '●' : ''}
                  </div>
                ))}
              </div>
              {/* PIN keypad */}
              <div className="grid grid-cols-3 gap-2">
                {['1','2','3','4','5','6','7','8','9','','0','⌫'].map((k, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => {
                      if (k === '⌫') setPin((p) => p.slice(0, -1));
                      else if (k !== '') handlePinKey(k);
                    }}
                    disabled={k === ''}
                    className={`h-12 rounded-xl text-base font-600 transition-all ${
                      k === '' ? 'invisible' :
                      k === '⌫'? 'bg-white/5 border border-white/8 text-white/50 hover:bg-white/10 hover:text-white/80' : 'bg-white/5 border border-white/8 text-white/80 hover:bg-violet-600/20 hover:border-violet-500/40 hover:text-white active:scale-95'
                    }`}
                  >
                    {k}
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 text-red-400 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !loginName || pin.length < 4}
              className="w-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 disabled:opacity-50 text-white font-600 py-3 rounded-xl transition-all duration-200 text-sm shadow-lg shadow-violet-600/20"
            >
              {loading ? 'Inloggen...' : 'Inloggen'}
            </button>
          </form>
        </div>

        {/* Navigation links */}
        <div className="flex items-center justify-between mt-6 px-1">
          <a href="/login" className="text-white/30 hover:text-white/60 text-sm transition-colors">
            ← Terug naar POS login
          </a>
          <a href="/portal/super-admin/login" className="text-white/20 hover:text-amber-500/60 text-xs transition-colors flex items-center gap-1">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            Super Admin
          </a>
        </div>
      </div>
    </div>
  );
}
